import {
    PREDICTION_AUTOMATIC,
    PREDICTION_OPTIONS_WITH_AUTO,
} from '@/config/runtimeStrategyOptions';
import {
    CLASSIC_METRICS_DEFAULTS,
    getMetricsInfoForScope,
} from '@/features/evaluation/components/Summarization/classicMetricsDefaults';
import {
    buildBuiltinCriteriaItems,
    usecaseToJudgeConfig,
} from '@/features/evaluation/components/Summarization/llmJudgeConfigUtils';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { rareClassEligibleLabels } from '@/shared/domain/labelPriority';
import { isNerTaskType } from '@/features/trainings/constants';
import { deriveIncludeInstructionInTraining } from '@/features/trainings/utils/trainingUtils';
import { formatSelectedBaseModelLabel } from '@/features/trainings/api/baseModelsApi';

const METRIC_LABELS = {
    recall: 'Recall',
    precision: 'Precision',
    accuracy: 'Accuracy',
    f_beta_score: 'F score',
};

function formatBeta(value) {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return '1';
    return String(Math.round(numeric * 10) / 10);
}

function predictionStrategyLabel(predictionStrategy) {
    const value = predictionStrategy || PREDICTION_AUTOMATIC;
    return (
        PREDICTION_OPTIONS_WITH_AUTO.find((opt) => opt.value === value)?.title || value
    );
}

function enabledJudgeCriteriaTitles(usecase) {
    const judgeConfig = usecaseToJudgeConfig(usecase);
    const builtinItems = buildBuiltinCriteriaItems(
        usecase?.mandatory_criteria_prompts || {},
        judgeConfig?.enabledById
    );
    const enabled = judgeConfig?.enabledById || {};
    return [...builtinItems, ...(judgeConfig?.custom || [])]
        .filter((item) => enabled[item.id] !== false && item.enabled !== false)
        .map((item) => item.title);
}

function enabledMetricsTitles(usecase, scope) {
    const classicConfig = {
        ...CLASSIC_METRICS_DEFAULTS,
        ...(usecase?.classic_metrics_config || {}),
    };
    return getMetricsInfoForScope(scope)
        .filter((m) => classicConfig?.[m.switchKey] !== false)
        .map((m) => m.title);
}

/**
 * Read-only Advanced parameter rows for Model Summary (mirrors Advanced.jsx
 * fields for the current task type).
 *
 * @returns {{ label: string, value: string }[]}
 */
export function buildAdvancedSettingsSummaryRows({
    taskType,
    trainingDevice,
    metricOptimization,
    betaForFScore,
    rareClassLabel,
    predictionStrategy,
    baseModel = null,
    usecase,
} = {}) {
    if (isGenerativeTask(taskType)) {
        const judgeTitles = enabledJudgeCriteriaTitles(usecase);
        const classicTitles = enabledMetricsTitles(usecase, 'classic');
        // Derived from the Model Configuration > SpecificAI model override
        // modes ("Disable" on both prompts turns this off).
        const generativeRows = [
            {
                label: 'Include prompt in training & evaluation',
                value: deriveIncludeInstructionInTraining({ ...usecase, task_type: taskType })
                    ? 'On'
                    : 'Off',
            },
        ];
        // Mirrors the Advanced accordion order: the Accuracy metrics section
        // (content generation only) sits above the LLM-as-a-judge section.
        if (taskType === TaskType.contentGeneration) {
            const accuracyTitles = enabledMetricsTitles(usecase, 'accuracy');
            generativeRows.push({
                label: 'Accuracy metrics criteria',
                value: accuracyTitles.length > 0 ? accuracyTitles.join(', ') : 'No metrics enabled',
            });
        }
        generativeRows.push(
            {
                label: 'LLM as a judge criteria',
                value: judgeTitles.length > 0 ? judgeTitles.join(', ') : 'No criteria enabled',
            },
            {
                label: 'Classic metrics criteria',
                value: classicTitles.length > 0 ? classicTitles.join(', ') : 'No metrics enabled',
            },
            {
                label: 'Base model',
                value: formatSelectedBaseModelLabel(baseModel),
            },
        );
        return generativeRows;
    }

    const rows = [
        {
            label: 'Training Device',
            value: String(trainingDevice || 'gpu').toUpperCase(),
        },
        {
            label: 'Metric Optimisation',
            value: METRIC_LABELS[metricOptimization] || metricOptimization || '—',
        },
    ];

    if (metricOptimization === 'f_beta_score') {
        rows.push({
            label: 'Beta',
            value: formatBeta(betaForFScore ?? 1),
        });
    }

    // "Optimize on" exists only where the Advanced picker does: single-label
    // classification with exactly two labels. A stale label (renamed since it
    // was picked) reads as the default, like the picker itself.
    const eligibleLabels = rareClassEligibleLabels({
        ...usecase,
        task_type: taskType ?? usecase?.task_type,
    });
    if (eligibleLabels.length > 0) {
        rows.push({
            label: 'Optimize on',
            value: rareClassLabel && eligibleLabels.includes(rareClassLabel) ? rareClassLabel : 'All labels',
        });
    }

    // Classification-only: NER serving ignores chunk aggregation / truncation.
    if (!isNerTaskType(taskType)) {
        rows.push({
            label: 'Prediction Strategy',
            value: predictionStrategyLabel(predictionStrategy),
        });
    }

    rows.push({
        label: 'Base model',
        value: formatSelectedBaseModelLabel(baseModel),
    });

    return rows;
}
